import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, CheckCircle, ArrowRight, Sparkles, Star, PartyPopper, Building2, Palette, ClipboardList, Truck, CreditCard, Shield, Headphones } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';

const benefits = {
  community: [
    'Support local artisans and small businesses',
    'Keep dollars circulating in your community',
    'Preserve traditional handmade craftsmanship',
    'Create jobs for local creative talent',
    'Foster meaningful maker-to-customer connections',
  ],
  venues: [
    '"Craft show in a box" ready to deploy',
    '100+ vetted artisan vendors',
    'Full event coordination',
    'Marketing and promotion included',
    'Turnkey solution for any space',
  ],
};

const venueEventTypes = [
  {
    icon: PartyPopper,
    title: 'Fundraisers',
    description: 'We bring the craft show to you so you don\'t have to organize one! Perfect for school or church organizations.',
  },
  {
    icon: Building2,
    title: 'Pop-Ups',
    description: 'Specially curated high quality handmade items to delight YOUR customers at restaurants, wineries, or retail spaces.',
  },
  {
    icon: Users,
    title: 'Community Events',
    description: 'Host a show with us for your next community, church, or school event!',
  },
  {
    icon: Palette,
    title: 'DIY Parties',
    description: 'Choose from over 20 different types of crafts for your next Sip-N-Such! From paint pouring to jewelry making.',
  },
  {
    icon: Building2,
    title: 'Team Building',
    description: 'We can bring a hands-on or virtual DIY activity for your next corporate event!',
  },
  {
    icon: Sparkles,
    title: 'Custom Events',
    description: 'Got an idea? Looking for something unique? We\'ll be happy to get creative with you!',
  },
];

const howItWorks = [
  {
    step: '01',
    title: 'Tell Us About Your Event',
    description: 'Share the details: size, location, indoor/outdoor, theme, etc. We\'ll work with you to book a date!',
    icon: ClipboardList,
  },
  {
    step: '02',
    title: 'We Curate the Perfect Selection',
    description: 'We handpick items from our Artisan family. High quality, handmade, local artisans - all guaranteed.',
    icon: Palette,
  },
  {
    step: '03',
    title: 'We Handle Everything',
    description: 'On event day, we bring tables, fixtures, our single checkout system, and gorgeous handmade items for shoppers.',
    icon: Truck,
  },
  {
    step: '04',
    title: 'Sit Back & Enjoy',
    description: 'Our crew manages the event, entertains customers, and handles all cleanup when it\'s over!',
    icon: PartyPopper,
  },
];

const whatWeHandle = [
  { icon: Users, text: 'Crew for setup & breakdown' },
  { icon: Headphones, text: 'Shopper assistance' },
  { icon: CreditCard, text: 'Credit card payments' },
  { icon: Shield, text: 'Insurance & vendor contracts' },
  { icon: ClipboardList, text: 'All back-office operations' },
];

const upcomingEvents = [
  {
    name: 'Festival of Trees at the Barn',
    date: 'November 2026',
    location: 'The Barn at Brambleton',
    type: 'Holiday Market',
    featured: true,
    link: '/events/festival-of-trees',
  },
];

export default function EventsPage() {
  return (
    <div className="page-transition" data-testid="events-page">
      <SEOHead page="events" />
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/66fd42e4-e3ff-4918-8fb8-52e8d98b4347/images/89f018d4ff66325e5424404841e9485d54d14e43d155fbd3c7599d2ae9fcf9a5.png"
            alt="Artisan Market Event"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 via-emerald-800/60 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Calendar className="w-4 h-4" />
              <span>AMS Events</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-semibold text-white mb-6 leading-tight">
              We Bring the Craft Show to You!
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8 max-w-2xl">
              Premier craft show organizers serving the DC Metro area, Virginia, Maryland, 
              West Virginia, Delaware, and Pennsylvania. We provide vendor opportunities 
              for artisans and turnkey "craft show in a box" solutions for venue owners.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="rounded-full px-8 py-6 text-lg bg-white text-emerald-700 hover:bg-white/90 btn-shine" data-testid="events-vendor-cta">
                  Become an AMS Vendor
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-lg border-white text-white hover:bg-white/10" data-testid="events-venue-cta">
                  Book Us for Your Next Event!
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-accent text-2xl">Who We Serve</span>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2">
              Two Options, One Mission
            </h2>
            <p className="text-lg text-muted-foreground mt-4 max-w-3xl mx-auto">
              When you partner with AMS, you're not just hosting an event—you're investing in your community. Our artisan markets bring local talent to your doorstep, keep dollars circulating in the local economy, and create meaningful connections between makers and neighbors.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Support Local Artisans */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white border border-border rounded-2xl p-8 shadow-sm"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-primary" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-serif font-semibold mb-4">Support Local Artisans</h3>
              <p className="text-muted-foreground mb-6">
                Every AMS event champions local talent and traditional craftsmanship. When you book with us, you're helping sustain the livelihoods of makers in your community and preserving handmade traditions for future generations.
              </p>
              <ul className="space-y-3 mb-8">
                {benefits.community.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full" data-testid="events-artisan-cta">
                  Book an Event with us!
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>

            {/* For Venues */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white border border-border rounded-2xl p-8 shadow-sm"
            >
              <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-accent" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-serif font-semibold mb-4">For Venue Owners</h3>
              <p className="text-muted-foreground mb-6">
                Host a turnkey craft show at your venue with our complete event solution.
              </p>
              <ul className="space-y-3 mb-8">
                {benefits.venues.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full" data-testid="events-venue-book-cta">
                  Book an Event with us!
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Venue Owners Get Section */}
      <section className="py-24 md:py-32 bg-muted/30" data-testid="venue-offerings-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-accent text-2xl">For Venue Owners</span>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2">
              What We Can Do For You
            </h2>
            <p className="text-lg text-muted-foreground mt-4 max-w-3xl mx-auto">
              What happens when you bring over 100 artisans together into a collective? You get access to incredibly talented people eager to bring YOU a hands-on, dazzling, and exceptional experience.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {venueEventTypes.map((type, index) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-border card-hover"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <type.icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-serif font-semibold mb-2">{type.title}</h3>
                <p className="text-muted-foreground">{type.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 md:py-32 bg-[#0F4C3A] text-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-amber-300 font-accent text-2xl">Simple Process</span>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-white mt-2">
              How It Works
            </h2>
            <p className="text-lg text-white/80 mt-4 max-w-2xl mx-auto">
              Ever thought of hosting an art or craft show? Whether your event is large or small, we're equipped with everything needed to bring a whole new artisan shopping experience to YOU!
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {howItWorks.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="relative"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 h-full border border-white/20">
                  <div className="text-amber-300 font-bold text-4xl mb-4">{step.step}</div>
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-white/70 text-sm">{step.description}</p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-amber-300/50" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* We Handle Everything */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
          >
            <h3 className="text-2xl font-serif font-semibold text-center mb-6">
              Let Us Do All the Heavy Lifting!
            </h3>
            <p className="text-white/80 text-center mb-8 max-w-3xl mx-auto">
              When you book an event with us, we'll cover everything. All you have to do is give us an internet connection and some space!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {whatWeHandle.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 bg-white/20 rounded-full px-4 py-2"
                >
                  <item.icon className="w-4 h-4 text-amber-300" />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/contact">
                <Button size="lg" className="rounded-full bg-amber-500 hover:bg-amber-400 text-[#0F4C3A] font-semibold" data-testid="how-it-works-cta">
                  Book an Event with us!
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Events - Simplified */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-accent font-accent text-2xl">Mark Your Calendar</span>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold text-foreground mt-2">
              Upcoming Events
            </h2>
          </motion.div>

          <div className="max-w-lg mx-auto">
            {upcomingEvents.map((event, index) => (
              <motion.div
                key={event.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white border rounded-2xl p-8 shadow-sm card-hover ${event.featured ? 'border-emerald-300 ring-2 ring-emerald-100' : 'border-border'}`}
              >
                {event.featured && (
                  <div className="flex items-center gap-1 text-emerald-600 text-sm font-medium mb-2">
                    <Star className="w-4 h-4" fill="currentColor" />
                    <span>Featured Event</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-accent text-sm font-medium mb-3">
                  <Sparkles className="w-4 h-4" />
                  <span>{event.type}</span>
                </div>
                <h3 className="text-2xl font-serif font-semibold mb-3">{event.name}</h3>
                <div className="space-y-2 text-muted-foreground mb-6">
                  <p className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {event.date}
                  </p>
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </p>
                </div>
                {event.link && (
                  <Link to={event.link}>
                    <Button className="w-full rounded-full" data-testid={`event-details-${index}`}>
                      View Event Details
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
